'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import Link from 'next/link'

type Path = 'exam' | 'independent'

export interface CalcState {
  path: Path
  firstName: string
  email: string
  clientHours: number
  indivHours: number
  groupHours: number
  cadIndivDyad: number
  cadGroup: number
  clientHoursPerMonth: number
  notifyCaseBuildingWorkshop: boolean
  crpoRegistrationDate: string
  targetExamSitting: string
  reportCreatedAt: string  // ISO string; set the first time user lands on Step 4
}

export interface CalculatorConfig {
  /** POST/PATCH/GET base for the calculator API. Default: /api/tools/crpo-calculator */
  apiBasePath?: string
  /** Prefix for the shareable results URL. After the initial POST returns an id, the URL
   *  is rewritten to `${resultsBasePath}/${id}`. Default: /tools/crpo-progress-calculator/results.
   *  Set to null to disable the rewrite (authenticated apps that route via /history instead). */
  resultsBasePath?: string | null
  /** Where the "Browse supervisors" CTA links (missing_cadence variant).
   *  Default: /between-sessions/supervisors. Empty string hides the button. */
  browseSupervisorsUrl?: string
  /** When set, seeds the identity fields on Step 1 (still editable unless hideIdentityFields). */
  prefillUser?: { firstName?: string; email?: string }
  /** Skip the identity input block on Step 1. Assumes prefillUser is authoritative (auth'd apps). */
  hideIdentityFields?: boolean
  /** Extra fields merged into the POST body when saving. Used to attach pro_id in auth'd apps. */
  extraSavePayload?: Record<string, unknown>
  /** Source tag saved with the row. Default: crpo-progress-calculator.
   *  Marks the row as belonging to the current-generation tool (vs legacy pre-2026 rows). */
  source?: string
  /** When true (default), the container uses -mt-20 pt-28 to sit UNDER a fixed transparent
   *  site header (marketing pattern). Auth'd apps with an in-flow nav should set this to false
   *  or the calculator will yank itself up behind the nav bar. */
  overlaySiteHeader?: boolean
}

const REQUIREMENTS: Record<Path, { client: number; supervision: number; indivMin: number; groupCap: number }> = {
  exam:        { client: 450,  supervision: 100, indivMin: 50, groupCap: 50 },
  independent: { client: 1000, supervision: 150, indivMin: 75, groupCap: 75 },
}

// Loose upper-bound used for tone-coding step 3's supervision cadence badge.
// Not a prescriptive target — CRPO's real recommendation is frequency-based
// (see SUPERVISION_FREQ_TARGET_* constants below).
const HEALTHY_MONTHLY_SUPERVISION_PACE = 10
// Between Sessions membership tiers.
const BS_COMPLETE_HOURS = 4.5      // hrs/mo of supervision on Complete
const BS_COMPLETE_PRICE = 295      // $/mo
const BS_PLUS_HOURS = 7.5          // hrs/mo of supervision on Complete Plus
const BS_PLUS_PRICE = 495          // $/mo
const MARKET_LOW = 150
const MARKET_HIGH = 200

// CRPO's current supervision-frequency guidance (as of March 2024, per Standard 4.2).
// Replaces the older ratio-based rule (which was 1:4.5 for RP(Q), 1:10 for RP).
// CRPO frames this as frequency (weekly / biweekly), not hours. We use the derived
// hrs/month as a MINIMUM floor for tone-coding — never as a prescriptive average.
const SUPERVISION_FREQ_TARGET_EXAM = 4        // min hrs/month at weekly frequency
const SUPERVISION_FREQ_TARGET_INDEPENDENT = 2 // min hrs/month at biweekly frequency

// CRPO holds the Registration Exam twice a year (spring + fall).
// Sittings past today are filtered out at render time — keep this list
// forward-looking; add new sittings as CRPO announces confirmed dates.
const EXAM_SITTINGS = [
  { value: '2026-04-28', label: 'Spring 2026' },
  { value: '2026-11-15', label: 'Fall 2026' },
  { value: '2027-04-15', label: 'Spring 2027' },
  { value: '2027-11-15', label: 'Fall 2027' },
  { value: '2028-04-15', label: 'Spring 2028' },
  { value: 'later',      label: 'Later or unsure' },
]

// Design tokens
const INK = '#1C1A2B'
const INK_BODY = '#3D3B54'    // body text — 8.4:1 on cream
const INK_META = '#5A5872'    // meta / label text — 5.9:1 on cream
const PURPLE = '#6C5CE7'
const PURPLE_DEEP = '#4C3FB8'
const PURPLE_PALE = '#EDEAFC'
const CREAM = '#F4F2FB'
const BORDER = '#E4E1F3'
const RED = '#B85C4A'
const RED_PALE = '#FBEDE9'
const GREEN = '#227A4D'

const DEFAULT_STATE: CalcState = {
  path: 'independent',
  firstName: '',
  email: '',
  clientHours: 0,
  indivHours: 0,
  groupHours: 0,
  cadIndivDyad: 0,
  cadGroup: 0,
  clientHoursPerMonth: 0,
  notifyCaseBuildingWorkshop: false,
  crpoRegistrationDate: '',
  targetExamSitting: '',
  reportCreatedAt: '',
}

function addMonths(date: Date, months: number): Date {
  const d = new Date(date)
  d.setMonth(d.getMonth() + months)
  return d
}

function formatMonthYear(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' , timeZone: 'America/Toronto'})
}

function computeDerived(state: CalcState) {
  const req = REQUIREMENTS[state.path]
  const groupCounted = Math.min(state.groupHours, req.groupCap)
  const totalSupervision = state.indivHours + groupCounted
  const remainingSupervision = Math.max(0, req.supervision - totalSupervision)
  const remainingClient = Math.max(0, req.client - state.clientHours)
  const monthlyCadence = state.cadIndivDyad + state.cadGroup

  const supervisionMonths = monthlyCadence > 0
    ? Math.ceil(remainingSupervision / monthlyCadence)
    : Infinity
  const clientMonths = state.clientHoursPerMonth > 0
    ? Math.ceil(remainingClient / state.clientHoursPerMonth)
    : Infinity

  const finiteSupervisionMonths = Number.isFinite(supervisionMonths) ? supervisionMonths : 0
  const finiteClientMonths = Number.isFinite(clientMonths) ? clientMonths : 0
  const currentMonths = Math.max(finiteSupervisionMonths, finiteClientMonths)

  const bottleneck: 'client' | 'supervision' = clientMonths > supervisionMonths ? 'client' : 'supervision'

  const now = new Date()
  const currentDate = addMonths(now, currentMonths)

  // Target exam sitting (exam path only, valid future date)
  const targetSitting = state.path === 'exam' && state.targetExamSitting && state.targetExamSitting !== 'later'
    ? state.targetExamSitting
    : null
  const targetDate = targetSitting ? new Date(targetSitting) : null
  const hasTarget = targetDate !== null && !Number.isNaN(targetDate.getTime())
  const msPerMonth = 1000 * 60 * 60 * 24 * 30
  const monthsUntilTarget = hasTarget
    ? Math.max(0, Math.round((targetDate!.getTime() - now.getTime()) / msPerMonth))
    : null
  // Positive = they'll finish AFTER their target (behind). Negative = ahead of target.
  const targetOverrunMonths = hasTarget && monthsUntilTarget !== null
    ? currentMonths - monthsUntilTarget
    : null
  const targetSittingLabel = hasTarget
    ? EXAM_SITTINGS.find(s => s.value === targetSitting)?.label ?? null
    : null

  return {
    req,
    totalSupervision,
    remainingSupervision,
    remainingClient,
    monthlyCadence,
    supervisionMonths,
    clientMonths,
    currentMonths,
    bottleneck,
    currentDate,
    // Target-aware fields (null when no valid target sitting)
    hasTarget,
    targetDate,
    targetSittingLabel,
    monthsUntilTarget,
    targetOverrunMonths,
  }
}

type RecVariant = 'missing_cadence' | 'missing_client_pace' | 'behind_pace' | 'on_track'

function computeRecommendation(state: CalcState, d: ReturnType<typeof computeDerived>): RecVariant {
  if (d.monthlyCadence === 0) return 'missing_cadence'
  if (state.clientHoursPerMonth === 0 && d.remainingClient > 0) return 'missing_client_pace'
  // Judge behind_pace only against a chosen target sitting.
  // Without a target, don't fabricate one — client hours often aren't in the therapist's control
  // (demand, referrals, insurance panels), so telling them they're "behind" is unfair.
  // 1-month buffer for exam prep (they should finish requirements ahead of the actual sitting).
  if (d.hasTarget && d.targetOverrunMonths !== null && d.targetOverrunMonths > 1) {
    return 'behind_pace'
  }
  return 'on_track'
}

function Donut({ current, required, size = 120 }: { current: number; required: number; size?: number }) {
  const pct = required > 0 ? Math.min(100, Math.round((current / required) * 100)) : 0
  const stroke = size < 100 ? 8 : 10
  const r = (size - stroke * 2) / 2
  const cx = size / 2
  const circ = 2 * Math.PI * r
  const offset = circ * (1 - pct / 100)
  const fontSize = size < 100 ? 18 : 24
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={cx} cy={cx} r={r} fill="none" stroke={BORDER} strokeWidth={stroke} />
      <circle
        cx={cx}
        cy={cx}
        r={r}
        fill="none"
        stroke={PURPLE}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={circ.toFixed(1)}
        strokeDashoffset={offset.toFixed(1)}
        transform={`rotate(-90 ${cx} ${cx})`}
        style={{ transition: 'stroke-dashoffset 600ms cubic-bezier(0.22, 1, 0.36, 1)' }}
      />
      <text
        x={cx}
        y={cx + fontSize / 3}
        textAnchor="middle"
        fontFamily="Inter, system-ui, sans-serif"
        fontSize={fontSize}
        fontWeight={800}
        fill={INK}
      >
        {pct}%
      </text>
    </svg>
  )
}

function ProgressStrip({ active }: { active: 1 | 2 | 3 }) {
  const steps: Array<{ n: 1 | 2 | 3; label: string }> = [
    { n: 1, label: "What You've Done" },
    { n: 2, label: "What You're Doing" },
    { n: 3, label: "Where You're Going" },
  ]
  return (
    <div className="mb-8">
      <div className="flex items-center gap-2 mb-3">
        {steps.map((s, i) => {
          const done = s.n < active
          const isActive = s.n === active
          const state = done ? 'done' : isActive ? 'active' : 'pending'
          return (
            <div key={s.n} className="flex items-center flex-1">
              <div
                className={`h-1.5 flex-1 rounded-full transition-colors ${
                  state === 'pending' ? 'bg-[#E4E1F3]' : 'bg-[#6C5CE7]'
                }`}
              />
              {i < steps.length - 1 && <div className="w-1" />}
            </div>
          )
        })}
      </div>
      <div className="flex items-center justify-between text-[11px] uppercase tracking-wider font-bold">
        {steps.map((s) => {
          const done = s.n < active
          const isActive = s.n === active
          const cls = done || isActive ? 'text-[#4C3FB8]' : 'text-[#8886A0]'
          return (
            <span key={s.n} className={`flex-1 ${s.n === 1 ? 'text-left' : s.n === 3 ? 'text-right' : 'text-center'} ${cls}`}>
              {s.label}
            </span>
          )
        })}
      </div>
    </div>
  )
}

export interface CrpoProgressCalculatorAppProps extends CalculatorConfig {
  initialStep?: 1 | 2 | 3 | 4
  initialState?: CalcState
  /** The DB row id when hydrating from a saved results URL. Enables PATCH updates. */
  initialSavedId?: string
  /** True when hydrating from a saved results URL — skips the auto-save on Step 4. */
  alreadySaved?: boolean
}

const DEFAULT_CONFIG: Required<Omit<CalculatorConfig, 'prefillUser' | 'extraSavePayload'>> = {
  apiBasePath: '/api/tools/crpo-calculator',
  resultsBasePath: '/tools/crpo-progress-calculator/results',
  browseSupervisorsUrl: '/between-sessions/supervisors',
  hideIdentityFields: false,
  source: 'crpo-progress-calculator',
  overlaySiteHeader: true,
}

export default function CalculatorApp({
  initialStep = 1,
  initialState,
  initialSavedId,
  alreadySaved = false,
  apiBasePath = DEFAULT_CONFIG.apiBasePath,
  resultsBasePath = DEFAULT_CONFIG.resultsBasePath,
  browseSupervisorsUrl = DEFAULT_CONFIG.browseSupervisorsUrl,
  prefillUser,
  hideIdentityFields = DEFAULT_CONFIG.hideIdentityFields,
  extraSavePayload,
  source = DEFAULT_CONFIG.source,
  overlaySiteHeader = DEFAULT_CONFIG.overlaySiteHeader,
}: CrpoProgressCalculatorAppProps = {}) {
  const seededState: CalcState = initialState ?? {
    ...DEFAULT_STATE,
    firstName: prefillUser?.firstName ?? DEFAULT_STATE.firstName,
    email: prefillUser?.email ?? DEFAULT_STATE.email,
  }
  const [step, setStep] = useState<1 | 2 | 3 | 4>(initialStep)
  const [state, setState] = useState<CalcState>(seededState)
  const [savedId, setSavedId] = useState<string | undefined>(initialSavedId)
  const update = (patch: Partial<CalcState>) => setState((s) => ({ ...s, ...patch }))

  const goToStep = (n: 1 | 2 | 3 | 4) => {
    // Stamp the report's creation date the first time they reach results.
    // Preserved across navigation back and forth; reset only on Start Over.
    if (n === 4 && !state.reportCreatedAt) {
      setState((s) => ({ ...s, reportCreatedAt: new Date().toISOString() }))
    }
    setStep(n)
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const containerWidth = step === 4 ? 'max-w-[1080px]' : 'max-w-[720px]'

  return (
    <div className={`min-h-screen bg-[#F4F2FB] font-sans pb-24 relative overflow-hidden ${overlaySiteHeader ? '-mt-20 pt-28' : 'pt-8'}`}>
      {step === 1 && (
        <>
          <div className="absolute -top-[240px] -right-[200px] w-[720px] h-[720px] bg-[radial-gradient(circle,rgba(108,92,231,0.18)_0%,transparent_65%)] rounded-full pointer-events-none" />
          <div className="absolute top-[300px] -left-[220px] w-[520px] h-[520px] bg-[radial-gradient(circle,rgba(139,126,240,0.14)_0%,transparent_65%)] rounded-full pointer-events-none" />
        </>
      )}

      <div className={`${containerWidth} mx-auto px-5 relative`}>
        {step === 1 && <Step1 state={state} update={update} onNext={() => goToStep(2)} hideIdentityFields={hideIdentityFields} />}
        {step === 2 && <Step2 state={state} update={update} onBack={() => goToStep(1)} onNext={() => goToStep(3)} />}
        {step === 3 && <Step3 state={state} update={update} onBack={() => goToStep(2)} onNext={() => goToStep(4)} />}
        {step === 4 && (
          <ResultsView
            state={state}
            update={update}
            onEdit={() => goToStep(2)}
            onEditPace={() => goToStep(3)}
            onRestart={() => { setState({ ...DEFAULT_STATE, firstName: prefillUser?.firstName ?? '', email: prefillUser?.email ?? '' }); setSavedId(undefined); goToStep(1) }}
            alreadySaved={alreadySaved}
            savedId={savedId}
            onSaved={setSavedId}
            apiBasePath={apiBasePath}
            resultsBasePath={resultsBasePath}
            browseSupervisorsUrl={browseSupervisorsUrl}
            extraSavePayload={extraSavePayload}
            source={source}
          />
        )}
      </div>
    </div>
  )
}

// ============================================================
// STEP 1 — Goal & identity
// ============================================================

function Step1({ state, update, onNext, hideIdentityFields = false }: { state: CalcState; update: (p: Partial<CalcState>) => void; onNext: () => void; hideIdentityFields?: boolean }) {
  const identityValid = state.firstName.trim().length > 0 && /\S+@\S+\.\S+/.test(state.email)
  const canProceed = hideIdentityFields ? true : identityValid
  return (
    <>
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 bg-white/70 backdrop-blur border border-[#E4E1F3] rounded-full pl-1.5 pr-4 py-1.5 shadow-[0_2px_12px_rgba(28,26,43,0.04)] mb-6">
          <span className="bg-gradient-to-r from-[#6C5CE7] to-[#4C3FB8] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">Free tool</span>
          <span className="text-[13px] font-medium text-[#5A5872]">Takes 3 minutes</span>
        </div>
        <h1 className="font-serif text-[42px] md:text-[52px] leading-[1.05] text-[#1C1A2B] tracking-tight mb-5">
          CRPO Progress <em className="not-italic text-[#6C5CE7] relative">
            Calculator
            <span className="absolute bottom-1 left-0 right-0 h-[4px] bg-[#6C5CE7]/25 rounded" />
          </em>
        </h1>
        <p className="text-[17px] leading-relaxed text-[#3D3B54] max-w-[500px] mx-auto">
          See how many hours you have left, what it&apos;ll cost, and how fast you can get there.
        </p>
      </div>

      <div className="bg-white rounded-[28px] p-8 sm:p-10 shadow-[0_8px_40px_rgba(28,26,43,0.08)] border border-white">
        <div className="text-[13px] uppercase tracking-widest font-bold text-[#4C3FB8] mb-5">What&apos;s your goal?</div>

        <div className="space-y-3 mb-8">
          {(['exam', 'independent'] as const).map((p) => {
            const selected = state.path === p
            const title = p === 'exam' ? 'Pass the CRPO Exam' : 'Practice Independently'
            const desc = p === 'exam'
              ? "I'm RP (Qualifying) and want to become a full RP."
              : "I'm a full RP and want to practice without a supervisor."
            const r = REQUIREMENTS[p]
            return (
              <button
                key={p}
                type="button"
                onClick={() => update({ path: p })}
                className={`w-full text-left border-2 rounded-2xl px-5 py-5 flex gap-4 items-start transition-all ${
                  selected
                    ? 'bg-[#F7F5FE] border-[#6C5CE7] shadow-[0_2px_16px_rgba(108,92,231,0.15)]'
                    : 'bg-white border-[#E4E1F3] hover:border-[#C4BAE8] hover:bg-[#FAFAFE]'
                }`}
              >
                <div className={`w-6 h-6 rounded-full border-2 flex-shrink-0 mt-0.5 flex items-center justify-center text-white text-xs transition-all ${
                  selected ? 'bg-[#6C5CE7] border-[#6C5CE7]' : 'border-[#C4BAE8] bg-white'
                }`}>
                  {selected && '✓'}
                </div>
                <div className="flex-1">
                  <div className="font-bold text-[17px] text-[#1C1A2B] mb-1">{title}</div>
                  <div className="text-[14px] text-[#3D3B54] mb-2 leading-relaxed">{desc}</div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-[13px]">
                    <span className="text-[#4C3FB8]"><strong>{r.client.toLocaleString()}</strong> client hrs</span>
                    <span className="text-[#4C3FB8]"><strong>{r.supervision}</strong> supervision hrs</span>
                    <span className="text-[#5A5872]">{p === 'exam' ? 'Exam required' : 'No exam'}</span>
                  </div>
                </div>
              </button>
            )
          })}
        </div>

        {!hideIdentityFields && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <Field label="First name">
              <input
                type="text"
                value={state.firstName}
                onChange={(e) => update({ firstName: e.target.value })}
                placeholder="Your first name"
                className={inputClass}
              />
            </Field>
            <Field label="Email">
              <input
                type="email"
                value={state.email}
                onChange={(e) => update({ email: e.target.value })}
                placeholder="you@email.com"
                className={inputClass}
              />
            </Field>
          </div>
        )}

        <button
          type="button"
          disabled={!canProceed}
          onClick={onNext}
          className="w-full py-4 rounded-full bg-gradient-to-r from-[#6C5CE7] to-[#4C3FB8] hover:shadow-[0_8px_28px_rgba(108,92,231,0.35)] text-white font-bold text-[16px] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none flex items-center justify-center gap-2"
        >
          Start Calculator
          <span aria-hidden>→</span>
        </button>

        {!hideIdentityFields && (
          <p className="text-center text-[12.5px] text-[#5A5872] mt-4">
            We&apos;ll email you your report so you can come back to it anytime.
          </p>
        )}
      </div>
    </>
  )
}

// ============================================================
// STEP 2 — What You've Done
// ============================================================

function Step2({ state, update, onBack, onNext }: { state: CalcState; update: (p: Partial<CalcState>) => void; onBack: () => void; onNext: () => void }) {
  const req = REQUIREMENTS[state.path]
  return (
    <div className="bg-white rounded-[28px] p-8 sm:p-10 shadow-[0_8px_40px_rgba(28,26,43,0.08)]">
      <ProgressStrip active={1} />

      <div className="flex items-center gap-4 mb-6">
        <div className="w-[54px] h-[54px] rounded-[14px] bg-gradient-to-br from-[#EDEAFC] to-[#DDD8F5] flex items-center justify-center text-[26px] shadow-[inset_0_1px_0_rgba(255,255,255,0.5)]">📊</div>
        <div>
          <h2 className="font-serif text-[26px] leading-tight text-[#1C1A2B]">
            Hi {state.firstName || 'there'}! Let&apos;s check your progress
          </h2>
          <p className="text-[14.5px] text-[#5A5872] mt-1">Slide or type your hours so far. Estimates are fine.</p>
        </div>
      </div>

      <div className="bg-gradient-to-br from-[#F7F5FE] to-[#EDEAFC] rounded-2xl px-5 py-4 mb-8 border border-[#E4E1F3]">
        <div className="text-[13px] font-bold text-[#4C3FB8] mb-2 uppercase tracking-wider">📋 {state.path === 'exam' ? 'CRPO Exam' : 'Independent Practice'} Requirements</div>
        <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-[14px] text-[#1C1A2B]">
          <span className="flex items-center gap-1.5"><Dot /><strong>{req.client}</strong>&nbsp;client hours</span>
          <span className="flex items-center gap-1.5"><Dot /><strong>{req.supervision}</strong>&nbsp;supervision hours</span>
          <span className="flex items-center gap-1.5"><Dot /><strong>{req.indivMin}+</strong>&nbsp;individual/dyadic</span>
        </div>
      </div>

      <div className="space-y-7 mb-7">
        <SliderInput
          label="Direct client contact hours"
          desc="Time spent in session with clients — individual, couples, family, or group."
          meta={`${req.client} required`}
          value={state.clientHours}
          max={req.client}
          onChange={(v) => update({ clientHours: v })}
        />
        <SliderInput
          label="Individual or dyadic supervision"
          desc="1–2 supervisees with one supervisor."
          meta={`${req.indivMin}+ required (of ${req.supervision})`}
          value={state.indivHours}
          max={req.supervision}
          onChange={(v) => update({ indivHours: v })}
        />
        <SliderInput
          label="Group supervision"
          desc="3+ supervisees with one supervisor."
          meta={`Up to ${req.groupCap} of ${req.supervision}`}
          value={state.groupHours}
          max={req.supervision}
          onChange={(v) => update({ groupHours: v })}
        />
      </div>

      {state.path === 'exam' && (
        <div className="bg-[#FAF9FE] rounded-2xl p-6 border border-[#E4E1F3] mb-7">
          <div className="text-[12px] font-bold text-[#4C3FB8] uppercase tracking-widest mb-4">Exam-specific</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="RP (Qualifying) registration date">
              <input
                type="date"
                value={state.crpoRegistrationDate}
                onChange={(e) => update({ crpoRegistrationDate: e.target.value })}
                className={inputClass}
              />
            </Field>
            <Field label="Target exam sitting">
              <select
                value={state.targetExamSitting}
                onChange={(e) => update({ targetExamSitting: e.target.value })}
                className={inputClass}
              >
                <option value="">Select a sitting</option>
                {EXAM_SITTINGS.filter(s => s.value === 'later' || new Date(s.value) >= new Date()).map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </Field>
          </div>
        </div>
      )}

      <StepNav onBack={onBack} onNext={onNext} nextLabel="Continue" />
    </div>
  )
}

// ============================================================
// STEP 3 — What You're Doing (cadence)
// ============================================================

function Step3({ state, update, onBack, onNext }: { state: CalcState; update: (p: Partial<CalcState>) => void; onBack: () => void; onNext: () => void }) {
  const req = REQUIREMENTS[state.path]
  const totalCadence = state.cadIndivDyad + state.cadGroup
  const totalSup = state.indivHours + Math.min(state.groupHours, req.groupCap)
  const remainingSup = Math.max(0, req.supervision - totalSup)
  const remainingClient = Math.max(0, req.client - state.clientHours)

  const supMonths = totalCadence > 0 ? Math.ceil(remainingSup / totalCadence) : null
  const clientMonths = state.clientHoursPerMonth > 0 ? Math.ceil(remainingClient / state.clientHoursPerMonth) : null
  const hasBoth = supMonths !== null && clientMonths !== null
  const currentMonths = hasBoth ? Math.max(supMonths, clientMonths) : null
  const previewDate = currentMonths !== null ? addMonths(new Date(), currentMonths) : null
  // Bottleneck: whichever finish takes longer is holding you back. "Balanced" if they're equal.
  const paceBottleneck: 'client' | 'supervision' | 'balanced' | null =
    hasBoth
      ? clientMonths > supMonths ? 'client'
        : supMonths > clientMonths ? 'supervision'
        : 'balanced'
      : null

  const supPaceTone: 'low' | 'ok' | 'high' =
    totalCadence === 0 ? 'low' :
    totalCadence < 4 ? 'low' :
    totalCadence >= HEALTHY_MONTHLY_SUPERVISION_PACE ? 'high' : 'ok'
  // No prescriptive tone for client hours — many therapists don't fully control this
  // (referrals, insurance panels, agency assignments). Just distinguish entered vs blank.
  const clientPaceTone: 'low' | 'ok' | 'high' =
    state.clientHoursPerMonth === 0 ? 'low' : 'ok'

  return (
    <div className="bg-white rounded-[28px] p-8 sm:p-10 shadow-[0_8px_40px_rgba(28,26,43,0.08)]">
      <ProgressStrip active={2} />

      <div className="flex items-center gap-4 mb-8">
        <div className="w-[54px] h-[54px] rounded-[14px] bg-gradient-to-br from-[#EDEAFC] to-[#DDD8F5] flex items-center justify-center text-[26px] shadow-[inset_0_1px_0_rgba(255,255,255,0.5)]">🚶</div>
        <div>
          <h2 className="font-serif text-[26px] leading-tight text-[#1C1A2B]">Now, how fast are you moving?</h2>
          <p className="text-[14.5px] text-[#5A5872] mt-1">Your monthly pace tells us your real timeline.</p>
        </div>
      </div>

      {/* Supervision card */}
      <div className="bg-gradient-to-br from-[#FAF9FE] to-[#F4F2FB] rounded-2xl p-6 sm:p-7 border border-[#E4E1F3] mb-5">
        <div className="flex items-baseline justify-between mb-1">
          <div className="font-bold text-[17px] text-[#1C1A2B]">Supervision hours per month</div>
          <PaceBadge tone={supPaceTone} value={totalCadence} unit="hrs/mo" />
        </div>
        <div className="text-[13.5px] text-[#5A5872] mb-6">
          Split it by type. Estimates are fine.
        </div>

        <div className="space-y-5">
          <SliderInput
            label="Individual or dyadic"
            value={state.cadIndivDyad}
            max={12}
            step={0.5}
            unit="hrs/mo"
            onChange={(v) => update({ cadIndivDyad: v })}
            compact
          />
          <SliderInput
            label="Group"
            value={state.cadGroup}
            max={12}
            step={0.5}
            unit="hrs/mo"
            onChange={(v) => update({ cadGroup: v })}
            compact
          />
        </div>

        {totalCadence === 0 && (
          <div className="mt-5 flex gap-3 items-start bg-white border border-[#C4BAE8] rounded-xl px-4 py-3.5 text-[13.5px] text-[#3D3B54]">
            <span className="text-base leading-none mt-0.5">💡</span>
            <div>
              <strong className="text-[#1C1A2B]">No supervisor yet? That&apos;s fine.</strong> We&apos;ll help you find one in your results.
            </div>
          </div>
        )}
      </div>

      {/* Client hours card */}
      <div className="bg-gradient-to-br from-[#FAF9FE] to-[#F4F2FB] rounded-2xl p-6 sm:p-7 border border-[#E4E1F3] mb-5">
        <div className="flex items-baseline justify-between mb-1">
          <div className="font-bold text-[17px] text-[#1C1A2B]">Client hours per month</div>
          <PaceBadge tone={clientPaceTone} value={state.clientHoursPerMonth} unit="hrs/mo" />
        </div>
        <div className="text-[13.5px] text-[#5A5872] mb-5">
          Client hours are often the real bottleneck. Enter roughly what you&apos;re seeing per month.
        </div>
        <SliderInput
          label="This month"
          value={state.clientHoursPerMonth}
          max={100}
          step={1}
          unit="hrs/mo"
          onChange={(v) => update({ clientHoursPerMonth: v })}
          compact
        />
      </div>

      {/* Live pace preview */}
      <PacePreview
        hasBoth={hasBoth}
        currentMonths={currentMonths}
        previewDate={previewDate}
        cadence={totalCadence}
        clientPace={state.clientHoursPerMonth}
        bottleneck={paceBottleneck}
      />

      <SupervisionCheckStrip
        path={state.path}
        monthlyCadence={totalCadence}
      />

      <StepNav onBack={onBack} onNext={onNext} nextLabel="See My Results" />
    </div>
  )
}

function PaceBadge({ tone, value, unit }: { tone: 'low' | 'ok' | 'high'; value: number; unit: string }) {
  const styles: Record<typeof tone, string> = {
    low:  'bg-[#F4F2FB] text-[#8886A0] border-[#E4E1F3]',
    ok:   'bg-[#EDEAFC] text-[#4C3FB8] border-[#C4BAE8]',
    high: 'bg-[#E4F5EA] text-[#227A4D] border-[#C0E5CE]',
  }
  const safe = Number.isFinite(value) ? value : 0
  const display = safe % 1 === 0 ? String(safe) : safe.toFixed(1)
  return (
    <span className={`inline-flex items-baseline gap-1 border rounded-full px-3 py-1 text-[13px] font-bold whitespace-nowrap ${styles[tone]}`}>
      {display} <span className="text-[11px] font-medium opacity-80">{unit}</span>
    </span>
  )
}

function SupervisionCheckStrip({
  path, monthlyCadence,
}: {
  path: Path
  monthlyCadence: number
}) {
  if (!Number.isFinite(monthlyCadence) || monthlyCadence <= 0) return null
  const target = path === 'exam' ? SUPERVISION_FREQ_TARGET_EXAM : SUPERVISION_FREQ_TARGET_INDEPENDENT
  const meets = monthlyCadence >= target
  const displayCadence = monthlyCadence % 1 === 0 ? String(monthlyCadence) : monthlyCadence.toFixed(1)
  const cadenceWord = path === 'exam' ? 'weekly' : 'biweekly'
  const roleLabel = path === 'exam' ? 'RP (Qualifying)' : 'RP working toward independent practice'
  return (
    <div
      className={`rounded-xl px-4 py-3 mb-6 border text-[13px] ${
        meets ? 'bg-[#E4F5EA] border-[#C0E5CE]' : 'bg-[#FBEDE9] border-[#F0C9BF]'
      }`}
    >
      <div className="flex items-center gap-3">
        <span className={`text-base leading-none ${meets ? 'text-[#227A4D]' : 'text-[#B85C4A]'}`}>{meets ? '✓' : '⚠'}</span>
        <div className="flex-1 leading-snug">
          <span className="font-bold text-[#1C1A2B]">Your monthly supervision: </span>
          <span className={`font-bold ${meets ? 'text-[#227A4D]' : 'text-[#B85C4A]'}`}>{displayCadence} hrs/mo</span>
          <span className="text-[#5A5872]"> · CRPO recommends {cadenceWord} (min. {target} hrs/mo)</span>
        </div>
        <span className={`whitespace-nowrap text-[11.5px] font-bold px-2 py-1 rounded-full ${meets ? 'bg-[#227A4D] text-white' : 'bg-[#B85C4A] text-white'}`}>
          {meets ? 'On cadence' : 'Below cadence'}
        </span>
      </div>
      <div className="text-[12px] text-[#5A5872] leading-snug mt-2 pl-7">
        CRPO recommends <strong className="text-[#1C1A2B]">{cadenceWord} supervision</strong> for {roleLabel} &mdash; a minimum of {target} hours per month.{' '}
        <a
          href="https://crpo.ca/resource-articles/standard-4-2-practising-with-clinical-supervision-supervision-frequency/"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-[#4C3FB8] hover:underline whitespace-nowrap"
        >
          Read the CRPO standard ↗
        </a>
      </div>
    </div>
  )
}

function PacePreview({
  hasBoth, currentMonths, previewDate, cadence, clientPace, bottleneck,
}: {
  hasBoth: boolean
  currentMonths: number | null
  previewDate: Date | null
  cadence: number
  clientPace: number
  bottleneck: 'client' | 'supervision' | 'balanced' | null
}) {
  if (!hasBoth || currentMonths === null || previewDate === null) {
    const needsWhat = cadence === 0 && clientPace === 0
      ? 'Add both amounts above'
      : cadence === 0 ? 'Add your supervision hours above'
      : 'Add your client hours above'
    return (
      <div className="rounded-2xl border border-dashed border-[#C4BAE8] bg-[#FAF9FE] px-5 py-4 mb-6 flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-[#EDEAFC] flex items-center justify-center text-sm">🎯</div>
        <div className="text-[13.5px] text-[#5A5872]">
          <strong className="text-[#1C1A2B]">{needsWhat}</strong> to preview your estimated finish date.
        </div>
      </div>
    )
  }
  return (
    <div className="relative bg-gradient-to-br from-[#F7F5FE] to-[#EDEAFC] border border-[#C4BAE8] rounded-2xl px-5 py-4 mb-6 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#6C5CE7] to-[#8B7EF0]" />
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-lg shadow-[0_2px_8px_rgba(76,63,184,0.15)] flex-shrink-0">🎯</div>
        <div className="flex-1">
          <div className="text-[11px] font-bold text-[#4C3FB8] uppercase tracking-wider mb-0.5">At this pace</div>
          <div className="text-[15.5px] text-[#1C1A2B]">
            You&apos;ll finish in <strong>~{currentMonths} months</strong> &mdash; around <strong>{formatMonthYear(previewDate)}</strong>
          </div>
          {currentMonths > 0 && bottleneck && (
            <div className="mt-2 flex items-start gap-2 text-[12.5px] text-[#3D3B54] leading-snug">
              <span className="text-base leading-none mt-px" aria-hidden>
                {bottleneck === 'client' ? '👤' : bottleneck === 'supervision' ? '🛡️' : '⚖️'}
              </span>
              <span>
                {bottleneck === 'balanced' ? (
                  <>Nice &mdash; your supervision and client hours are <strong className="text-[#1C1A2B]">in balance</strong>. Both finish at about the same time.</>
                ) : (
                  <>Bottleneck: <strong className="text-[#1C1A2B]">{bottleneck === 'client' ? 'client hours' : 'supervision hours'}</strong>. That&apos;s what&apos;s setting your finish date right now.</>
                )}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ============================================================
// STEP 4 — Results
// ============================================================

function ResultsView({
  state, update, onEdit, onEditPace, onRestart, alreadySaved = false, savedId, onSaved,
  apiBasePath, resultsBasePath, browseSupervisorsUrl, extraSavePayload, source,
}: {
  state: CalcState
  update: (patch: Partial<CalcState>) => void
  onEdit: () => void
  onEditPace: () => void
  onRestart: () => void
  alreadySaved?: boolean
  savedId?: string
  onSaved?: (id: string) => void
  apiBasePath: string
  resultsBasePath: string | null
  browseSupervisorsUrl: string
  extraSavePayload?: Record<string, unknown>
  source: string
}) {
  const derived = useMemo(() => computeDerived(state), [state])
  const variant = useMemo(() => computeRecommendation(state, derived), [state, derived])
  const goalLabel = state.path === 'exam' ? 'CRPO Exam' : 'Independent Practice'

  // Save the report once, then redirect to the canonical /results/[id] URL so the
  // report is bookmarkable and shareable. Skips both when loaded FROM the results URL.
  const savedRef = useRef(false)
  useEffect(() => {
    if (alreadySaved) return
    if (savedRef.current) return
    if (!state.firstName || !state.email) return
    savedRef.current = true
    fetch(apiBasePath, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        firstName: state.firstName,
        email: state.email,
        path: state.path,
        // Cumulative hours (Step 2)
        clientHours: state.clientHours > 0 ? state.clientHours : null,
        supervisionIndividual: state.indivHours > 0 ? state.indivHours : null,
        supervisionGroup: state.groupHours > 0 ? state.groupHours : null,
        // Monthly pace (Step 3)
        cadIndivDyad: state.cadIndivDyad > 0 ? state.cadIndivDyad : null,
        cadGroup: state.cadGroup > 0 ? state.cadGroup : null,
        clientHoursPerMonth: state.clientHoursPerMonth > 0 ? state.clientHoursPerMonth : null,
        // Exam path
        targetExam: state.targetExamSitting || null,
        crpoRegistrationDate: state.crpoRegistrationDate || null,
        notifyCaseBuildingWorkshop: state.notifyCaseBuildingWorkshop,
        source,
        ...(extraSavePayload ?? {}),
      }),
    })
      .then((res) => res.ok ? res.json() : null)
      .then((data: { id?: string } | null) => {
        if (data?.id) {
          onSaved?.(data.id)
          if (typeof window !== 'undefined' && resultsBasePath) {
            // Replace history entry so back-button doesn't return them to the wizard.
            window.history.replaceState(null, '', `${resultsBasePath}/${data.id}`)
          }
        }
      })
      .catch((err) => {
        // Non-blocking: user still sees results even if the save fails.
        console.error('[CRPO Calculator] Failed to save results:', err)
      })
    // Intentionally empty deps — save only once per Step 4 landing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Debounced PATCH for any field the user can change on the results view.
  // Fires whenever savedId is known OR the patchable state changes. No "skip
  // first" guard on purpose — that guard used to swallow user clicks made
  // BEFORE the initial POST returned, since savedId only becomes defined a bit
  // after mount. A redundant PATCH on hydration is harmless (writes back the
  // same values). Debounced so slider drags collapse into one write.
  const patchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (!savedId) return
    if (patchTimerRef.current) clearTimeout(patchTimerRef.current)
    patchTimerRef.current = setTimeout(() => {
      fetch(`${apiBasePath}/${savedId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cadIndivDyad: state.cadIndivDyad,
          cadGroup: state.cadGroup,
          clientHoursPerMonth: state.clientHoursPerMonth,
          notifyCaseBuildingWorkshop: state.notifyCaseBuildingWorkshop,
        }),
      }).catch((err) => {
        console.error('[CRPO Calculator] Failed to sync changes:', err)
      })
    }, 600)
    return () => {
      if (patchTimerRef.current) clearTimeout(patchTimerRef.current)
    }
  }, [savedId, state.cadIndivDyad, state.cadGroup, state.clientHoursPerMonth, state.notifyCaseBuildingWorkshop])

  return (
    <>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-[12px] font-bold text-[#4C3FB8] uppercase tracking-widest mb-2">Between Sessions</div>
          <h1 className="font-serif text-[34px] md:text-[40px] leading-[1.1] tracking-tight text-[#1C1A2B]">
            {state.firstName ? `${state.firstName}'s ` : 'Your '}<em className="not-italic text-[#6C5CE7]">{goalLabel}</em> Progress Report
          </h1>
          <p className="text-[15px] text-[#3D3B54] mt-2">
            Here&apos;s where you stand on your path to {state.path === 'exam' ? 'the CRPO exam' : 'independent practice'}.
          </p>
          {state.reportCreatedAt && (
            <p className="text-[12.5px] text-[#5A5872] mt-1.5">
              Generated on {new Date(state.reportCreatedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' , timeZone: 'America/Toronto'})}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => window.print()}
          className="self-start sm:self-auto inline-flex items-center gap-2 bg-white border border-[#E4E1F3] px-4 py-2.5 rounded-full font-semibold text-[13px] text-[#1C1A2B] hover:border-[#C4BAE8] hover:shadow-[0_2px_10px_rgba(28,26,43,0.06)] transition-all whitespace-nowrap"
        >
          <span aria-hidden>🖨️</span> Print report
        </button>
      </div>

      {/* Target exam sitting — surfaces user's chosen finish line */}
      {state.path === 'exam' && state.targetExamSitting && (
        <TargetExamSittingBanner value={state.targetExamSitting} />
      )}

      {/* Recommendation card comes first — it's the payload */}
      <RecommendationCard
        variant={variant}
        state={state}
        derived={derived}
        onFixClientPace={onEditPace}
        onChange={update}
        browseSupervisorsUrl={browseSupervisorsUrl}
      />

      {/* Donut row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <DonutCard
          icon="👤"
          eyebrow={`Required: ${derived.req.client} hrs`}
          title="Direct Client Contact"
          current={state.clientHours}
          max={derived.req.client}
          fractionSuffix={`/ ${derived.req.client}`}
        />
        <DonutCard
          icon="🛡️"
          eyebrow={`Required: ${derived.req.supervision} hrs · ${derived.req.indivMin}+ individual`}
          title="Supervision Hours"
          current={derived.totalSupervision}
          max={derived.req.supervision}
          fractionSuffix={`/ ${derived.req.supervision}`}
        />
        <DonutCard
          icon="🛡️"
          eyebrow={`CRPO: ${state.path === 'exam' ? 'weekly min.' : 'biweekly min.'}`}
          title="Monthly Supervision"
          current={state.cadIndivDyad + state.cadGroup}
          max={state.path === 'exam' ? SUPERVISION_FREQ_TARGET_EXAM : SUPERVISION_FREQ_TARGET_INDEPENDENT}
          fractionSuffix={`/ ${state.path === 'exam' ? SUPERVISION_FREQ_TARGET_EXAM : SUPERVISION_FREQ_TARGET_INDEPENDENT}+`}
          captionOverride="Current cadence"
          unit="hrs/mo"
        />
      </div>

      {/* Supervision frequency check — CRPO's current guidance */}
      <SupervisionCheckCard
        path={state.path}
        monthlyCadence={state.cadIndivDyad + state.cadGroup}
      />

      {/* Two-column: Need + Cost */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
        <NeedCard remainingClient={derived.remainingClient} remainingSupervision={derived.remainingSupervision} />
        <CostCard remaining={derived.remainingSupervision} />
      </div>

      {/* Case-building callout */}
      {derived.remainingClient > 0 && (
        <div className="bg-gradient-to-br from-[#F7F5FE] to-[#EDEAFC] rounded-2xl p-6 sm:p-7 mb-6 flex gap-5 border border-[#DDD8F5]">
          <div className="w-[52px] h-[52px] min-w-[52px] rounded-[14px] bg-white flex items-center justify-center text-2xl shadow-[0_2px_10px_rgba(76,63,184,0.10)]">🌱</div>
          <div className="flex-1">
            <div className="font-serif font-bold text-[20px] text-[#1C1A2B] mb-2 leading-tight">Building a caseload is hard. That&apos;s normal.</div>
            <p className="text-[14.5px] text-[#3D3B54] leading-relaxed mb-4">
              Finding and keeping clients is a skill no one teaches you. Between Sessions membership includes practice-building resources so you don&apos;t have to figure it out alone.
            </p>
            <button
              type="button"
              onClick={() => update({ notifyCaseBuildingWorkshop: !state.notifyCaseBuildingWorkshop })}
              className={`inline-flex items-center gap-2.5 px-4 py-2.5 rounded-full text-[13.5px] font-semibold transition-all ${
                state.notifyCaseBuildingWorkshop
                  ? 'bg-[#6C5CE7] text-white shadow-[0_2px_10px_rgba(108,92,231,0.25)]'
                  : 'bg-white border border-[#C4BAE8] text-[#4C3FB8] hover:border-[#6C5CE7]'
              }`}
            >
              <span
                className={`w-4 h-4 rounded flex items-center justify-center text-[10px] transition-all ${
                  state.notifyCaseBuildingWorkshop ? 'bg-white text-[#6C5CE7]' : 'border-[1.5px] border-[#C4BAE8] text-transparent'
                }`}
              >
                ✓
              </span>
              Notify me about the next caseload workshop
            </button>
          </div>
        </div>
      )}

      {state.path === 'exam' && state.crpoRegistrationDate && (
        <FiveYearDeadlineBanner regDate={state.crpoRegistrationDate} />
      )}

      {/* Your Inputs — secondary info at bottom */}
      <div className="bg-white rounded-2xl p-6 mb-4 border border-[#E4E1F3]">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-[15px] text-[#1C1A2B]">Your inputs</h3>
          <button
            type="button"
            onClick={onEdit}
            className="text-[13px] font-semibold text-[#4C3FB8] hover:text-[#6C5CE7] transition-colors"
          >
            ✏️ Edit
          </button>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <InputCell num={state.clientHours} label="Direct client contact" />
          <InputCell num={state.indivHours} label="Individual/dyad supervision" />
          <InputCell num={state.groupHours} label="Group supervision" />
        </div>
      </div>

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onRestart}
          className="flex-1 px-5 py-3.5 rounded-full border border-[#E4E1F3] bg-white text-[#3D3B54] font-semibold text-[14px] hover:border-[#C4BAE8] hover:bg-[#FAFAFE] transition-all"
        >
          ↺ Start over
        </button>
      </div>

    </>
  )
}

// ============================================================
// Reusable components
// ============================================================

const inputClass =
  'w-full px-4 py-3 border-[1.5px] border-[#E4E1F3] rounded-xl text-[15px] text-[#1C1A2B] bg-white placeholder:text-[#8886A0] focus:border-[#6C5CE7] focus:ring-[3px] focus:ring-[#6C5CE7]/15 outline-none transition-all'

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-[13px] font-bold text-[#1C1A2B] mb-2">{label}</label>
      {children}
    </div>
  )
}

function Dot() {
  return <span className="w-1.5 h-1.5 rounded-full bg-[#6C5CE7]" />
}

function StepNav({ onBack, onNext, nextLabel }: { onBack: () => void; onNext: () => void; nextLabel: string }) {
  return (
    <div className="flex gap-3 mt-2">
      <button
        type="button"
        onClick={onBack}
        className="px-6 py-4 rounded-full border-[1.5px] border-[#E4E1F3] bg-white text-[#3D3B54] font-semibold text-[15px] hover:bg-[#FAFAFE] hover:border-[#C4BAE8] transition-all"
      >
        ← Back
      </button>
      <button
        type="button"
        onClick={onNext}
        className="flex-1 py-4 rounded-full bg-gradient-to-r from-[#6C5CE7] to-[#4C3FB8] hover:shadow-[0_8px_28px_rgba(108,92,231,0.35)] text-white font-bold text-[16px] transition-all flex items-center justify-center gap-2"
      >
        {nextLabel}
        <span aria-hidden>→</span>
      </button>
    </div>
  )
}

function SliderInput({
  label, desc, meta, value, max, onChange, unit = 'hrs', step = 1, compact = false,
}: {
  label: string
  desc?: string
  meta?: string
  value: number
  max: number
  onChange: (v: number) => void
  unit?: string
  step?: number
  compact?: boolean
}) {
  const safeValue = Number.isFinite(value) ? value : 0
  const pct = max > 0 ? Math.min(100, (safeValue / max) * 100) : 0
  return (
    <div>
      <div className={`flex justify-between items-baseline ${compact ? 'mb-2' : 'mb-1.5'}`}>
        <span className={`font-bold text-[#1C1A2B] ${compact ? 'text-[14px]' : 'text-[15.5px]'}`}>{label}</span>
        {meta && <span className="text-[12px] font-bold text-[#4C3FB8] uppercase tracking-wide">{meta}</span>}
      </div>
      {desc && <div className="text-[13.5px] text-[#3D3B54] mb-4 leading-relaxed">{desc}</div>}
      <div className="flex items-center gap-4">
        <div className="flex-1 relative h-6 flex items-center">
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-2 rounded-full bg-[#E4E1F3] pointer-events-none" />
          <div
            className="absolute top-1/2 -translate-y-1/2 left-0 h-2 rounded-full bg-gradient-to-r from-[#6C5CE7] to-[#8B7EF0] pointer-events-none transition-[width]"
            style={{ width: `calc(${pct}% + ${11 - pct * 0.22}px)` }}
          />
          <input
            type="range"
            min={0}
            max={max}
            step={step}
            value={safeValue}
            onChange={(e) => {
              const n = Number(e.target.value)
              onChange(Number.isFinite(n) ? n : 0)
            }}
            className="relative w-full appearance-none bg-transparent cursor-pointer h-6 m-0 p-0 slider-thumb z-10"
          />
        </div>
        <div className="flex items-center gap-1.5">
          <input
            type="number"
            min={0}
            max={max}
            step={step}
            value={safeValue}
            onChange={(e) => {
              const raw = e.target.value
              if (raw === '') { onChange(0); return }
              const n = Number(raw)
              onChange(Number.isFinite(n) ? Math.min(max, Math.max(0, n)) : 0)
            }}
            className={`px-2.5 py-2 border-[1.5px] border-[#E4E1F3] rounded-lg text-[14px] text-center font-bold text-[#1C1A2B] bg-white focus:border-[#6C5CE7] focus:ring-[3px] focus:ring-[#6C5CE7]/15 outline-none transition-all ${compact ? 'w-[64px]' : 'w-[72px]'}`}
          />
          <span className="text-[12px] font-medium text-[#5A5872] whitespace-nowrap">{unit}</span>
        </div>
      </div>
      <style jsx>{`
        .slider-thumb {
          -webkit-appearance: none;
          appearance: none;
        }
        .slider-thumb::-webkit-slider-runnable-track {
          height: 24px;
          background: transparent;
          border: none;
        }
        .slider-thumb::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: white;
          border: 3px solid ${PURPLE};
          box-shadow: 0 2px 8px rgba(108, 92, 231, 0.35);
          cursor: pointer;
          transition: transform 120ms ease;
          margin-top: 1px;
        }
        .slider-thumb::-webkit-slider-thumb:hover { transform: scale(1.1); }
        .slider-thumb::-moz-range-track {
          height: 24px;
          background: transparent;
          border: none;
        }
        .slider-thumb::-moz-range-thumb {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: white;
          border: 3px solid ${PURPLE};
          box-shadow: 0 2px 8px rgba(108, 92, 231, 0.35);
          cursor: pointer;
        }
      `}</style>
    </div>
  )
}

function DonutCard({
  icon, eyebrow, title, current, max, fractionSuffix, captionOverride, unit = 'hrs',
}: {
  icon: string
  eyebrow: string
  title: string
  current: number
  max: number
  fractionSuffix: string
  captionOverride?: string
  unit?: string
}) {
  return (
    <div className="bg-white rounded-2xl p-6 border border-[#E4E1F3] shadow-[0_2px_14px_rgba(28,26,43,0.04)]">
      <div className="flex justify-between items-start mb-1">
        <div className="flex-1 pr-2">
          <div className="text-[11px] font-bold text-[#4C3FB8] uppercase tracking-wider leading-tight">{eyebrow}</div>
          <div className="text-[16px] font-bold text-[#1C1A2B] mt-1.5 leading-tight">{title}</div>
        </div>
        <div className="w-[38px] h-[38px] rounded-[10px] bg-[#EDEAFC] flex items-center justify-center text-[17px] flex-shrink-0">{icon}</div>
      </div>
      <div className="flex justify-center my-4">
        <Donut current={current} required={max} size={120} />
      </div>
      <div className="text-center text-[12.5px] text-[#5A5872] font-medium">{captionOverride ?? 'Current progress'}</div>
      <div className="text-center text-[19px] font-bold text-[#1C1A2B] mt-1">
        {current} <span className="text-[13px] font-medium text-[#5A5872]">{fractionSuffix} {unit}</span>
      </div>
    </div>
  )
}

function SupervisionCheckCard({
  path, monthlyCadence,
}: {
  path: Path
  monthlyCadence: number
}) {
  const target = path === 'exam' ? SUPERVISION_FREQ_TARGET_EXAM : SUPERVISION_FREQ_TARGET_INDEPENDENT
  const cadenceWord = path === 'exam' ? 'weekly' : 'biweekly'
  const roleLabel = path === 'exam' ? 'RP (Qualifying)' : 'RP working toward independent practice'
  const hasCadence = Number.isFinite(monthlyCadence) && monthlyCadence > 0
  const meets = hasCadence && monthlyCadence >= target
  const displayCadence = hasCadence ? (monthlyCadence % 1 === 0 ? String(monthlyCadence) : monthlyCadence.toFixed(1)) : '—'

  return (
    <div className="bg-white rounded-2xl p-6 sm:p-7 border border-[#E4E1F3] shadow-[0_2px_14px_rgba(28,26,43,0.04)] mb-6">
      <div className="flex items-baseline justify-between gap-3 mb-1.5">
        <h3 className="font-serif text-[22px] font-bold text-[#1C1A2B] leading-tight">Are you supervised often enough?</h3>
        <span className="text-[11.5px] font-bold text-[#4C3FB8] uppercase tracking-wider whitespace-nowrap">
          CRPO: {cadenceWord}
        </span>
      </div>
      <p className="text-[13.5px] text-[#3D3B54] mb-5 leading-relaxed">
        CRPO recommends <strong className="text-[#1C1A2B]">{cadenceWord} supervision</strong> for {roleLabel} therapists &mdash; a minimum of {target} hours per month.{' '}
        <a
          href="https://crpo.ca/resource-articles/standard-4-2-practising-with-clinical-supervision-supervision-frequency/"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-[#4C3FB8] hover:underline whitespace-nowrap"
        >
          Read the standard ↗
        </a>
      </p>

      {!hasCadence ? (
        <div className="rounded-xl px-5 py-4 border border-dashed border-[#C4BAE8] bg-[#FAF9FE]">
          <div className="text-[11.5px] font-bold text-[#4C3FB8] uppercase tracking-wider mb-2">Your monthly supervision</div>
          <div className="text-[13px] text-[#5A5872]">Enter your monthly cadence on step 3 to see this check.</div>
        </div>
      ) : (
        <div className={`rounded-xl px-5 py-4 flex items-center justify-between gap-4 ${meets ? 'bg-[#E4F5EA]' : 'bg-[#FBEDE9]'}`}>
          <div>
            <div className="text-[11.5px] font-bold uppercase tracking-wider mb-1.5 text-[#1C1A2B]/70">Your monthly supervision</div>
            <div className="font-serif text-[26px] font-bold text-[#1C1A2B] leading-none">
              {displayCadence} <span className="font-sans text-[15px] font-medium text-[#5A5872]">hrs / month</span>
            </div>
            <div className="text-[12.5px] text-[#5A5872] mt-1.5">
              CRPO minimum: {target} hrs/mo ({cadenceWord})
            </div>
          </div>
          <span className={`whitespace-nowrap text-[11.5px] font-bold px-3 py-1.5 rounded-full ${meets ? 'bg-[#227A4D] text-white' : 'bg-[#B85C4A] text-white'}`}>
            {meets ? '✓ On cadence' : '⚠ Below cadence'}
          </span>
        </div>
      )}
    </div>
  )
}

function NeedCard({ remainingClient, remainingSupervision }: { remainingClient: number; remainingSupervision: number }) {
  return (
    <div className="bg-white rounded-2xl p-6 border border-[#E4E1F3] shadow-[0_2px_14px_rgba(28,26,43,0.04)]">
      <h3 className="font-serif text-[22px] font-bold text-[#1C1A2B] mb-5 leading-tight">What you still need</h3>
      <div className="space-y-3">
        <div className="bg-[#EDEAFC] rounded-xl px-5 py-4">
          <div className="text-[11.5px] font-bold text-[#4C3FB8] uppercase tracking-wider mb-2 flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#4C3FB8]" /> Direct client contact
          </div>
          <div className="font-serif text-[28px] font-bold text-[#1C1A2B] leading-none">
            {remainingClient} <span className="font-sans text-[14px] font-medium text-[#5A5872]">hours to go</span>
          </div>
        </div>
        <div className="bg-[#EDEAFC] rounded-xl px-5 py-4">
          <div className="text-[11.5px] font-bold text-[#4C3FB8] uppercase tracking-wider mb-2 flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#4C3FB8]" /> Supervision hours
          </div>
          <div className="font-serif text-[28px] font-bold text-[#1C1A2B] leading-none">
            {remainingSupervision} <span className="font-sans text-[14px] font-medium text-[#5A5872]">hours to go</span>
          </div>
        </div>
      </div>
    </div>
  )
}

function CostCard({ remaining }: { remaining: number }) {
  const marketLow = Math.round(remaining * MARKET_LOW)
  const marketHigh = Math.round(remaining * MARKET_HIGH)
  const bsMonths = remaining > 0 ? Math.ceil(remaining / BS_COMPLETE_HOURS) : 0
  const bsCost = bsMonths * BS_COMPLETE_PRICE
  const savings = Math.max(0, marketLow - bsCost)

  return (
    <div className="bg-white rounded-2xl p-6 border border-[#E4E1F3] shadow-[0_2px_14px_rgba(28,26,43,0.04)]">
      <div className="flex items-baseline justify-between mb-1 gap-2">
        <h3 className="font-serif text-[22px] font-bold text-[#1C1A2B] leading-tight">What will it cost?</h3>
        {savings > 0 && (
          <span className="text-[11.5px] font-bold text-[#227A4D] bg-[#E4F5EA] px-2 py-1 rounded-full whitespace-nowrap">
            Save ~${savings.toLocaleString()}
          </span>
        )}
      </div>
      <p className="text-[13.5px] text-[#3D3B54] mb-5">
        Estimated cost for your remaining <strong className="text-[#1C1A2B]">{remaining}</strong> supervision hours.
      </p>

      <div className="space-y-3">
        {/* Market rate reference */}
        <div className="rounded-xl p-4 bg-[#FAFAFE] border border-[#E4E1F3]">
          <div className="flex items-baseline justify-between mb-1">
            <div className="font-bold text-[14px] text-[#1C1A2B]">Typical market rate</div>
            <div className="text-[12px] text-[#5A5872]">${MARKET_LOW}–${MARKET_HIGH}/hr</div>
          </div>
          <div className="font-serif text-[24px] font-bold text-[#1C1A2B] leading-tight">
            ${marketLow.toLocaleString()}–${marketHigh.toLocaleString()}
          </div>
        </div>

        {/* Between Sessions — Complete */}
        <div className="rounded-xl p-4 bg-gradient-to-br from-[#F7F5FE] to-[#EDEAFC] border border-[#C4BAE8]">
          <div className="flex items-baseline justify-between mb-1 gap-2">
            <div className="font-bold text-[14px] text-[#1C1A2B]">Between Sessions Complete</div>
            <div className="text-[12px] text-[#4C3FB8] whitespace-nowrap">${BS_COMPLETE_PRICE}/mo &middot; ~{bsMonths} mo</div>
          </div>
          <div className="text-[12.5px] text-[#3D3B54] mb-1.5">
            Includes <strong className="text-[#1C1A2B]">{BS_COMPLETE_HOURS} hrs of supervision per month</strong>
          </div>
          <div className="font-serif text-[24px] font-bold text-[#1C1A2B] leading-tight">
            ${bsCost.toLocaleString()}
          </div>
          <p className="text-[12px] text-[#5A5872] mt-2 leading-snug">
            Need to move faster? Add extra supervision hours à la carte, or upgrade to Complete Plus.
          </p>
        </div>
      </div>
    </div>
  )
}

function RecommendationCard({
  variant, state, derived, onFixClientPace, onChange, browseSupervisorsUrl,
}: {
  variant: RecVariant
  state: CalcState
  derived: ReturnType<typeof computeDerived>
  onFixClientPace: () => void
  onChange: (patch: Partial<CalcState>) => void
  browseSupervisorsUrl: string
}) {
  const goalLabel = state.path === 'exam' ? 'exam readiness' : 'independent practice'

  if (variant === 'missing_cadence') {
    return (
      <RecShell tone="warn" eyebrow="No supervision yet" eyebrowIcon="⚠️">
        <RecHeadline>You haven&apos;t started supervision yet.</RecHeadline>
        <RecBody>
          Without steady supervision, your hours won&apos;t add up. Even a small, steady pace gets you there years faster than starting and stopping.
        </RecBody>
        {browseSupervisorsUrl && <RecCTA href={browseSupervisorsUrl}>Browse supervisors</RecCTA>}
      </RecShell>
    )
  }

  if (variant === 'missing_client_pace') {
    return (
      <RecShell tone="warn" eyebrow="One thing missing" eyebrowIcon="⚠️">
        <RecHeadline>We don&apos;t know your client pace yet.</RecHeadline>
        <RecBody>
          Client hours are often the real bottleneck — not supervision. Add yours to get an accurate timeline.
        </RecBody>
        <RecCTAButton onClick={onFixClientPace}>Add my client hours</RecCTAButton>
      </RecShell>
    )
  }

  if (variant === 'behind_pace') {
    const bottleneckLabel = derived.bottleneck === 'client' ? 'client hours' : 'supervision hours'
    const overrun = derived.targetOverrunMonths ?? 0
    return (
      <RecShell tone="warn" eyebrow={derived.hasTarget ? 'Behind your target' : 'Slow pace'} eyebrowIcon="⚠️">
        <RecHeadline>
          {derived.hasTarget && derived.targetSittingLabel ? (
            <>You&apos;re on pace to finish about <span className="text-[#B85C4A]">{overrun} {overrun === 1 ? 'month' : 'months'} after {derived.targetSittingLabel}</span>.</>
          ) : (
            <>At this pace, you won&apos;t reach {goalLabel} until <span className="text-[#B85C4A]">{formatMonthYear(derived.currentDate)}</span>.</>
          )}
        </RecHeadline>
        <RecBody>
          Projected finish: <strong className="text-[#1C1A2B]">{formatMonthYear(derived.currentDate)}</strong>. Your{' '}<strong className="text-[#1C1A2B]">{bottleneckLabel}</strong>{' '}are what&apos;s holding you back &mdash; try adjusting the numbers below to see what could get you back on target.
        </RecBody>
        <InlineAdjuster state={state} derived={derived} onChange={onChange} />
      </RecShell>
    )
  }

  return (
    <RecShell tone="ok" eyebrow={derived.hasTarget ? 'On track for your target' : "You're on track"} eyebrowIcon="✓">
      <RecHeadline>
        {derived.hasTarget && derived.targetSittingLabel ? (
          <>You&apos;ll finish in time for <span className="text-[#6C5CE7]">{derived.targetSittingLabel}</span>.</>
        ) : (
          <>You&apos;re on pace for <span className="text-[#6C5CE7]">{formatMonthYear(derived.currentDate)}</span>.</>
        )}
      </RecHeadline>
      <RecBody>
        {derived.hasTarget ? (
          <>Projected finish: <strong className="text-[#1C1A2B]">{formatMonthYear(derived.currentDate)}</strong>. Curious how much faster you could go &mdash; or what happens if you ease off? Try different numbers below.</>
        ) : (
          <>Your pace is solid. Curious how much faster you could go &mdash; or what happens if you ease off? Try different numbers below.</>
        )}
      </RecBody>
      <InlineAdjuster state={state} derived={derived} onChange={onChange} />
    </RecShell>
  )
}

function RecShell({
  tone, eyebrow, eyebrowIcon, children,
}: { tone: 'warn' | 'ok'; eyebrow: string; eyebrowIcon: string; children: React.ReactNode }) {
  const stripe = tone === 'warn' ? 'from-[#B85C4A] to-[#D97757]' : 'from-[#6C5CE7] to-[#8B7EF0]'
  const eyebrowColor = tone === 'warn' ? 'text-[#B85C4A]' : 'text-[#4C3FB8]'
  return (
    <div className="relative bg-white rounded-2xl p-7 sm:p-8 mb-6 shadow-[0_4px_24px_rgba(28,26,43,0.08)] overflow-hidden border border-[#E4E1F3]">
      <div className={`absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r ${stripe}`} />
      <div className={`text-[12px] font-bold uppercase tracking-widest mb-3 flex items-center gap-2 ${eyebrowColor}`}>
        <span aria-hidden>{eyebrowIcon}</span> {eyebrow}
      </div>
      {children}
    </div>
  )
}

function RecHeadline({ children }: { children: React.ReactNode }) {
  return <div className="font-serif text-[24px] sm:text-[28px] font-bold text-[#1C1A2B] leading-tight mb-3">{children}</div>
}

function RecBody({ children }: { children: React.ReactNode }) {
  return <p className="text-[15px] text-[#3D3B54] leading-relaxed mb-5">{children}</p>
}

function RecCTA({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Link
      href={href}
      className="inline-flex items-center gap-2 py-3.5 px-6 rounded-full bg-gradient-to-r from-[#6C5CE7] to-[#4C3FB8] hover:shadow-[0_8px_28px_rgba(108,92,231,0.35)] text-white font-bold text-[15px] transition-all"
    >
      {children} <span aria-hidden>→</span>
    </Link>
  )
}

function RecCTAButton({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-2 py-3.5 px-6 rounded-full bg-gradient-to-r from-[#6C5CE7] to-[#4C3FB8] hover:shadow-[0_8px_28px_rgba(108,92,231,0.35)] text-white font-bold text-[15px] transition-all"
    >
      {children} <span aria-hidden>→</span>
    </button>
  )
}

function InputCell({ num, label }: { num: number; label: string }) {
  return (
    <div className="bg-[#FAFAFE] rounded-xl p-4 text-center border border-[#E4E1F3]">
      <div className="font-serif text-[24px] font-bold text-[#1C1A2B] leading-none">{num}</div>
      <div className="text-[11.5px] text-[#5A5872] mt-1.5 leading-tight">{label}</div>
    </div>
  )
}

function InlineAdjuster({
  state, derived, onChange,
}: {
  state: CalcState
  derived: ReturnType<typeof computeDerived>
  onChange: (patch: Partial<CalcState>) => void
}) {
  const [expanded, setExpanded] = useState(false)

  const totalCadence = state.cadIndivDyad + state.cadGroup
  const clientPace = state.clientHoursPerMonth
  const bothEntered = totalCadence > 0 && clientPace > 0

  const supMonths = totalCadence > 0 ? Math.ceil(derived.remainingSupervision / totalCadence) : Infinity
  const clientMonths = clientPace > 0 ? Math.ceil(derived.remainingClient / clientPace) : Infinity
  const finiteSup = Number.isFinite(supMonths) ? supMonths : 0
  const finiteClient = Number.isFinite(clientMonths) ? clientMonths : 0
  const previewMonths = Math.max(finiteSup, finiteClient)
  const previewDate = addMonths(new Date(), previewMonths)

  const bottleneck: 'client' | 'supervision' | 'balanced' =
    finiteClient > finiteSup ? 'client'
    : finiteSup > finiteClient ? 'supervision'
    : 'balanced'

  return (
    <div className="mt-6 pt-6 border-t border-[#E4E1F3] space-y-4">
      {/* Preview — always visible */}
      <div className="rounded-xl bg-gradient-to-br from-[#F7F5FE] to-[#EDEAFC] border border-[#C4BAE8] p-4">
        {bothEntered && previewMonths > 0 ? (
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center text-base shadow-[0_2px_8px_rgba(76,63,184,0.10)] flex-shrink-0">🎯</div>
            <div className="flex-1">
              <div className="text-[11px] font-bold text-[#4C3FB8] uppercase tracking-wider mb-0.5">At this pace</div>
              <div className="text-[15px] text-[#1C1A2B]">
                <strong>~{previewMonths} months</strong> &mdash; around <strong>{formatMonthYear(previewDate)}</strong>
              </div>
              <div className="mt-1.5 flex items-start gap-1.5 text-[12.5px] text-[#3D3B54] leading-snug">
                <span className="text-sm leading-none mt-px" aria-hidden>
                  {bottleneck === 'client' ? '👤' : bottleneck === 'supervision' ? '🛡️' : '⚖️'}
                </span>
                <span>
                  {bottleneck === 'balanced'
                    ? <>Nice &mdash; supervision and client hours are <strong className="text-[#1C1A2B]">in balance</strong>.</>
                    : <>Bottleneck: <strong className="text-[#1C1A2B]">{bottleneck === 'client' ? 'client hours' : 'supervision hours'}</strong>.</>
                  }
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <span className="text-base" aria-hidden>🎯</span>
            <div className="text-[13px] text-[#5A5872]">
              Add supervision <strong className="text-[#1C1A2B]">and</strong> client hours to see a projection.
            </div>
          </div>
        )}
      </div>

      {/* Collapsible slider controls */}
      {!expanded ? (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="w-full flex items-center justify-between gap-4 px-5 py-3 rounded-xl bg-white border border-[#E4E1F3] hover:border-[#C4BAE8] hover:bg-[#FAFAFE] transition-all group text-left"
        >
          <div>
            <div className="text-[13.5px] font-semibold text-[#1C1A2B]">Try different numbers</div>
            <div className="text-[12px] text-[#5A5872] mt-0.5">Adjust your pace to see what could change</div>
          </div>
          <span className="text-[#4C3FB8] text-sm font-bold flex-shrink-0 transition-transform group-hover:translate-y-0.5" aria-hidden>▼</span>
        </button>
      ) : (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="text-[11px] font-bold text-[#4C3FB8] uppercase tracking-widest">Try different numbers</div>
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="text-[12px] font-semibold text-[#5A5872] hover:text-[#1C1A2B] transition-colors flex items-center gap-1"
            >
              Hide <span aria-hidden>▲</span>
            </button>
          </div>

          <div className="space-y-4">
            <SliderInput
              label="Individual/Dyadic supervision"
              value={state.cadIndivDyad}
              max={12}
              step={0.5}
              unit="hrs/mo"
              onChange={(v) => onChange({ cadIndivDyad: v })}
              compact
            />
            <SliderInput
              label="Group supervision"
              value={state.cadGroup}
              max={12}
              step={0.5}
              unit="hrs/mo"
              onChange={(v) => onChange({ cadGroup: v })}
              compact
            />
            <SliderInput
              label="Client hours"
              value={state.clientHoursPerMonth}
              max={100}
              step={1}
              unit="hrs/mo"
              onChange={(v) => onChange({ clientHoursPerMonth: v })}
              compact
            />
          </div>
        </div>
      )}
    </div>
  )
}


function TargetExamSittingBanner({ value }: { value: string }) {
  const sitting = EXAM_SITTINGS.find((s) => s.value === value)
  if (!sitting) return null

  const isDated = value !== 'later'
  const sittingDate = isDated ? new Date(value) : null
  const isFuture = sittingDate ? sittingDate.getTime() > Date.now() : false
  const monthsUntil =
    sittingDate && isFuture
      ? Math.max(1, Math.round((sittingDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24 * 30)))
      : null

  return (
    <div className="relative bg-gradient-to-br from-[#F7F5FE] to-[#EDEAFC] border border-[#C4BAE8] rounded-2xl px-5 py-4 mb-6 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#6C5CE7] to-[#8B7EF0]" />
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-lg shadow-[0_2px_8px_rgba(76,63,184,0.15)]">🎯</div>
        <div className="flex-1">
          <div className="text-[11px] font-bold text-[#4C3FB8] uppercase tracking-wider mb-0.5">Target exam sitting</div>
          <div className="text-[15.5px] text-[#1C1A2B]">
            {isDated ? (
              <>
                <strong>{sitting.label}</strong>
                {monthsUntil !== null && ` — ~${monthsUntil} ${monthsUntil === 1 ? 'month' : 'months'} away`}
                {sittingDate && !isFuture && ' — sitting has passed'}
              </>
            ) : (
              <strong>Not sure yet — that&apos;s fine.</strong>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function FiveYearDeadlineBanner({ regDate }: { regDate: string }) {
  const reg = new Date(regDate)
  if (Number.isNaN(reg.getTime())) return null
  const deadline = new Date(reg)
  deadline.setFullYear(deadline.getFullYear() + 5)
  const now = new Date()
  const msPerMonth = 1000 * 60 * 60 * 24 * 30
  const monthsRemaining = Math.max(0, Math.round((deadline.getTime() - now.getTime()) / msPerMonth))
  const urgent = monthsRemaining <= 12
  return (
    <div className={`rounded-2xl p-6 mb-6 border-[1.5px] ${
      urgent ? 'bg-[#FBEDE9] border-[#F0C9BF]' : 'bg-gradient-to-br from-[#F7F5FE] to-[#EDEAFC] border-[#C4BAE8]'
    }`}>
      <div className={`text-[12px] font-bold uppercase tracking-widest mb-2 ${urgent ? 'text-[#B85C4A]' : 'text-[#4C3FB8]'}`}>
        {urgent ? '⚠️ 5-year deadline approaching' : '📅 5-year exam window'}
      </div>
      <div className="text-[16px] font-bold text-[#1C1A2B] mb-1.5">
        You must pass the CRPO exam within 5 years of RP (Qualifying) registration.
      </div>
      <div className="text-[14px] text-[#3D3B54]">
        Deadline: <strong className="text-[#1C1A2B]">{deadline.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' , timeZone: 'America/Toronto'})}</strong>
        {' — '}
        {monthsRemaining > 0 ? `${monthsRemaining} months remaining` : 'deadline has passed'}
      </div>
    </div>
  )
}
